<script setup>
/**
 * Whether a match's (or a ladder challenge's) result goes to an external
 * service such as Squash Levels: a small block per integration the API
 * lists, on the match page and the ladder challenge page ONLY (owner, 30 Oct
 * 2026, mockup v7; never in lists or cards, pinned by
 * external-service-marks.test.ts).
 *
 * Each integration is a ROW in the style of the other rows on the challenge
 * page (owner, 2 Nov 2026: the "Message Beth" row): a 42px tile holding the
 * integration's logo (greyed when not sending), a bold "Sending to {name}" /
 * "Not sending to {name}", the reason the API sent written out underneath,
 * and, when the reason is the viewer's own setting and the match has no
 * result yet, a Turn on that opens the question in place (owner, 3 Nov 2026,
 * SQUASH_LEVELS_CARDS_PLAN.md; it used to send them to their profile). Once a
 * result is in: words only, no button, no switch. `changed` fires after
 * turning on, so the page refetches the mark.
 * No hover needed. An integration this build has no logo for shows a neutral
 * icon in its tile. Every word comes from useExternalServiceMarks.js.
 *
 * Also the Send challenge form's preview of the challenge being written
 * (ladder page, kind "challenge"), from GET ladderChallenges/externalServicesPreview.
 */
import {computed, ref} from 'vue';
import {externalServiceDetailMarks} from '~/composables/useExternalServiceMarks';
import SquashLevelsTurnOnDialog from '~/components/squashLevels/SquashLevelsTurnOnDialog.vue';

const props = defineProps({
  item: {type: Object, default: null},
  kind: {type: String, default: 'match'},
  // The match already has a result: no Turn on (owner, 3 Nov 2026).
  hasResult: {type: Boolean, default: false},
});
const emit = defineEmits(['changed']);

const marks = computed(() => externalServiceDetailMarks(props.item, props.kind, {hasResult: props.hasResult}));
const turnOnKey = ref(null);
const turnOnOpen = computed({
  get: () => turnOnKey.value != null,
  set: (v) => { if (!v) turnOnKey.value = null; },
});
</script>

<template>
  <div v-if="marks.length" class="esr-list">
    <div v-for="m in marks" :key="m.service" class="tl-card esr" :class="{'esr--off': !m.sending}"
         :data-service="m.service" data-testid="external-service-row">
      <span class="esr-ic" aria-hidden="true">
        <img v-if="m.logo" :src="m.logo.src" alt="" width="22" height="22" class="esr-img"/>
        <v-icon v-else icon="mdi-link-variant" size="20" class="esr-generic"/>
      </span>
      <span class="esr-body">
        <span class="esr-t">{{ m.label }}</span>
        <span class="esr-s">{{ m.reason }}</span>
      </span>
      <v-btn v-if="m.turnOn" color="primary" variant="flat" size="small" rounded="pill" class="esr-turn-on"
             data-testid="external-service-turn-on" @click="turnOnKey = m.turnOn.questionKey">{{ m.turnOn.label }}</v-btn>
    </div>
    <squash-levels-turn-on-dialog v-if="turnOnKey" v-model="turnOnOpen" :question-key="turnOnKey"
                                  @saved="emit('changed')"/>
  </div>
</template>

<style scoped lang="scss">
/* The same row as the challenge page's chat row (.chat-row): a tl-card,
   a 42px tile, a 13.5px title and an 11.5px line under it. */
.esr-list { display: flex; flex-direction: column; gap: 8px; }
.esr {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  text-align: left;
}
/* The logo always sits on white: the Squash Levels mark is made for it. */
.esr-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-charcoal));
}
.esr-img { display: block; width: 22px; height: 22px; }
.esr--off .esr-img, .esr--off .esr-generic { filter: grayscale(1); opacity: .5; }
.esr-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.esr-t { font-size: 13.5px; font-weight: 600; color: rgb(var(--v-theme-text)); }
.esr-s { font-size: 11.5px; line-height: 1.45; color: rgb(var(--v-theme-textSubtle)); }
.esr-turn-on { flex: 0 0 auto; }
</style>
